import { useEffect, useMemo, useState } from "react";
import { Box, Paper, Typography, Stack, CircularProgress } from "@mui/material";
import {
  // TrendingUpRounded,
  PeopleRounded,
  SupportAgentRounded,
  SchoolRounded,
  CheckCircleOutlineRounded,
} from "@mui/icons-material";
import { keyframes } from "@mui/system";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { adminService } from "../services/adminService";
import {
  MESSAGES,
  primaryColor,
  accentColor,
  successColor,
  infoColor,
  warningColor,
  errorColor,
} from "../../../constants/consts";

/* ─────────────── Animasyonlar ─────────────── */
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ─────────────── Sabitler ─────────────── */
const MONTHS_TR = [
  "Oca",
  "Şub",
  "Mar",
  "Nis",
  "May",
  "Haz",
  "Tem",
  "Ağu",
  "Eyl",
  "Eki",
  "Kas",
  "Ara",
];

const PIE_COLORS = {
  Gönderildi: warningColor,
  işlemde: infoColor,
  Beklemede: errorColor,
  Tamamlandı: successColor,
};

/* ─────────────── Tarih parse — iki formatı da destekler ─────────────── */
function parseApiDate(input) {
  if (!input) return null;
  // 1) ISO / normal format
  try {
    const d = new Date(input);
    if (!isNaN(d.getTime())) return d;
  } catch {}
  // 2) "EEE, dd MMM yyyy HH:mm:ss z" (Flutter formatı)
  try {
    const cleaned = String(input).replace(/^[A-Za-z]{3},\s*/, "");
    const d = new Date(cleaned);
    if (!isNaN(d.getTime())) return d;
  } catch {}
  return null;
}

/* ─────────────── Son 6 ayın etiketlerini üret ─────────────── */
function last6Months() {
  const now = new Date();
  const out = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push({
      key: `${d.getFullYear()}-${d.getMonth()}`,
      label: MONTHS_TR[d.getMonth()],
      year: d.getFullYear(),
      month: d.getMonth(),
    });
  }
  return out;
}

/* ═══════════════════════════════════════════════════════════
   ANA BİLEŞEN
   ═══════════════════════════════════════════════════════════ */
export default function AdminHome({ user }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [supports, setSupports] = useState([]);
  const [supporters, setSupporters] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [edus, setEdus] = useState([]);

  /* ─────────── Veri yükleme ─────────── */
  useEffect(() => {
    (async () => {
      setLoading(true);
      setError("");
      try {
        const [sup, sList, cList, rList, eList] = await Promise.all([
          adminService.getSupports().catch(() => []),
          adminService
            .getUsersByType({ type: "destek", sunucuKodu: "" })
            .catch(() => []),
          adminService
            .getUsersByType({ type: "user", sunucuKodu: "" })
            .catch(() => []),
          adminService
            .getUsersByType({ type: "rootUser", sunucuKodu: "" })
            .catch(() => []),
          adminService.getEdus().catch(() => []),
        ]);
        setSupports(Array.isArray(sup) ? sup : []);
        setSupporters(Array.isArray(sList) ? sList : []);
        setCustomers([...cList, ...rList]);
        setEdus(Array.isArray(eList) ? eList : []);
      } catch (e) {
        console.error(e);
        setError("Dashboard verileri yüklenemedi.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /* ─────────── KPI hesapları ─────────── */
  const kpis = useMemo(() => {
    const total = supports.length;
    const completed = supports.filter((s) =>
      String(s.status || "")
        .toLowerCase()
        .includes("tamam"),
    ).length;
    const completionRate = total ? Math.round((completed / total) * 100) : 0;

    return [
      {
        icon: SupportAgentRounded,
        label: "Toplam Talep",
        value: total,
        color: primaryColor,
      },
      {
        icon: PeopleRounded,
        label: "Aktif Kullanıcı",
        value: supporters.length + customers.length,
        color: infoColor,
      },
      {
        icon: SchoolRounded,
        label: "Toplam Eğitim",
        value: edus.length,
        color: accentColor,
      },
      {
        icon: CheckCircleOutlineRounded,
        label: "Tamamlanma Oranı",
        value: `${completionRate}%`,
        color: successColor,
      },
    ];
  }, [supports, supporters, customers, edus]);

  /* ─────────── Aylık talep akışı (son 6 ay) ─────────── */
  const monthlyData = useMemo(() => {
    const months = last6Months();
    const counts = months.map(() => 0);

    supports.forEach((s) => {
      const d = parseApiDate(s.created_at);
      if (!d) return;
      const idx = months.findIndex(
        (m) => m.year === d.getFullYear() && m.month === d.getMonth(),
      );
      if (idx >= 0) counts[idx]++;
    });

    return months.map((m, i) => ({
      ay: m.label,
      talep: counts[i],
    }));
  }, [supports]);

  /* ─────────── Sunucu bazlı dağılım (top 6) ─────────── */
  const serverData = useMemo(() => {
    const map = new Map();
    supports.forEach((s) => {
      const key = s.sunucu_kodu || "Bilinmiyor";
      map.set(key, (map.get(key) || 0) + 1);
    });

    return Array.from(map.entries())
      .map(([sunucu, deger]) => ({ sunucu, deger }))
      .sort((a, b) => b.deger - a.deger)
      .slice(0, 6);
  }, [supports]);
  const RADIAN = Math.PI / 180;

  const renderLabel = ({
    cx,
    cy,
    midAngle,
    outerRadius,
    name,
    value,
    percent,
    fill, // ← dilimin rengi
  }) => {
    const radius = outerRadius + 28;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    const isRight = x > cx;

    return (
      <text
        x={x}
        y={y}
        textAnchor={isRight ? "start" : "end"}
        dominantBaseline="central"
        fontSize={12}
        fill={fill}
      >
        <tspan x={x} dy="-0.5em" fontWeight={700}>
          {name}
        </tspan>
        <tspan x={x} dy="1.2em" fontWeight={600}>
          {value} · {Math.round(percent * 100)}%
        </tspan>
      </text>
    );
  };
  /* ─────────── Personel performansı (tamamlanan, top 6) ─────────── */
  const supporterPerf = useMemo(() => {
    const map = new Map();
    supports.forEach((s) => {
      const isDone = String(s.status || "")
        .toLowerCase()
        .includes("tamam");
      if (!isDone) return;
      const key = s.supporter && s.supporter !== "Havuz" ? s.supporter : null;
      if (!key) return;
      map.set(key, (map.get(key) || 0) + 1);
    });

    return Array.from(map.entries())
      .map(([personel, tamamlanan]) => ({ personel, tamamlanan }))
      .sort((a, b) => b.tamamlanan - a.tamamlanan)
      .slice(0, 6);
  }, [supports]);

  /* ─────────── Durum dağılımı (Pie) ─────────── */
  const statusData = useMemo(() => {
    const counts = {
      Gönderildi: 0,
      işlemde: 0,
      Beklemede: 0,
      Tamamlandı: 0,
    };
    supports.forEach((s) => {
      const st = s.status || "";
      if (counts[st] !== undefined) counts[st]++;
    });
    return Object.entries(counts)
      .map(([name, value]) => ({ name, value }))
      .filter((d) => d.value > 0);
  }, [supports]);

  /* ─────────── Render ─────────── */
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <CircularProgress sx={{ color: "#fff" }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        gap: 2,
        overflowY: "auto",
        pb: 2,
        "&::-webkit-scrollbar": { width: 6 },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "rgba(255,255,255,0.20)",
          borderRadius: 3,
        },
      }}
    >
      {/* Başlık */}
      <Box
        sx={{
          animation: `${fadeInUp} 0.4s ease-out both`,
          flexShrink: 0,
        }}
      >
        <Typography variant="h5" fontWeight={800} color="#fff">
          {MESSAGES.ADMIN_HOME_TITLE}
        </Typography>
        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)" }}>
          {MESSAGES.ADMIN_HOME_SUBTITLE}
        </Typography>
      </Box>

      {error && (
        <Typography sx={{ color: "#FCA5A5", fontSize: 13, fontWeight: 600 }}>
          {error}
        </Typography>
      )}

      {/* KPI Kartları */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        sx={{ flexShrink: 0 }}
      >
        {kpis.map((kpi, i) => (
          <KpiCard key={i} {...kpi} delay={0.1 + i * 0.06} />
        ))}
      </Stack>

      {/* Aylık Talep Akışı — Line */}
      <ChartCard
        title="Aylık Talep Akışı"
        subtitle="Son 6 ayda oluşturulan destek talepleri"
        delay={0.35}
      >
        <ResponsiveContainer width="100%" height={280}>
          <LineChart
            data={monthlyData}
            margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis dataKey="ay" stroke="#64748B" fontSize={12} />
            <YAxis stroke="#64748B" fontSize={12} allowDecimals={false} />
            <Tooltip
              contentStyle={{
                borderRadius: 8,
                border: "1px solid #E2E8F0",
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              }}
            />
            <Line
              type="monotone"
              dataKey="talep"
              name="Talep"
              stroke={primaryColor}
              strokeWidth={3}
              dot={{ r: 4, fill: primaryColor }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* İki kolonlu grafikler */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 2,
          flexShrink: 0,
        }}
      >
        {/* Sunucu bazlı dağılım */}
        <ChartCard
          title="Sunucu Bazlı Dağılım"
          subtitle="En çok talep açan 6 sunucu"
          delay={0.45}
        >
          {serverData.length === 0 ? (
            <EmptyChart />
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart
                data={serverData}
                margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis
                  dataKey="sunucu"
                  stroke="#64748B"
                  fontSize={11}
                  interval={0}
                  angle={-20}
                  textAnchor="end"
                  height={60}
                />
                <YAxis stroke="#64748B" fontSize={12} allowDecimals={false} />
                <Tooltip
                  contentStyle={{
                    borderRadius: 8,
                    border: "1px solid #E2E8F0",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  }}
                />
                <Bar
                  dataKey="deger"
                  name="Talep"
                  fill={accentColor}
                  radius={[6, 6, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        {/* Durum dağılımı */}
        <ChartCard
          title="Talep Durum Dağılımı"
          subtitle="Taleplerin güncel durumları"
          delay={0.5}
        >
          {statusData.length === 0 ? (
            <EmptyChart />
          ) : (
            // <ResponsiveContainer width="100%" height={280}>
            //   <PieChart>
            //     <Pie
            //       data={statusData}
            //       cx="50%"
            //       cy="50%"
            //       innerRadius={55}
            //       outerRadius={90}
            //       paddingAngle={3}
            //       dataKey="value"
            //       label={({ name, value, percent }) =>
            //         `${name} (${value} · ${Math.round(percent * 100)}%)`
            //       }
            //       fontSize={12}
            //       labelLine={{ stroke: "#94A3B8" }}
            //     >
            //       {statusData.map((entry, idx) => (
            //         <Cell
            //           key={idx}
            //           fill={PIE_COLORS[entry.name] || "#94A3B8"}
            //         />
            //       ))}
            //     </Pie>
            //     <Tooltip
            //       contentStyle={{
            //         borderRadius: 8,
            //         border: "1px solid #E2E8F0",
            //         boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            //       }}
            //     />
            //     <Legend
            //       verticalAlign="bottom"
            //       iconType="circle"
            //       wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
            //     />
            //   </PieChart>
            // </ResponsiveContainer>
            <ResponsiveContainer width="100%" height={360}>
              <PieChart margin={{ top: 30, right: 70, bottom: 30, left: 70 }}>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="45%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={4}
                  minAngle={8}
                  dataKey="value"
                  label={renderLabel}
                  labelLine={{ stroke: "#94A3B8" }}
                >
                  {statusData.map((entry, idx) => (
                    <Cell
                      key={idx}
                      fill={PIE_COLORS[entry.name] || "#94A3B8"}
                    />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    borderRadius: 8,
                    border: "1px solid #E2E8F0",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  }}
                />
                <Legend
                  verticalAlign="bottom"
                  iconType="circle"
                  wrapperStyle={{ fontSize: 12, paddingTop: 16 }}
                />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </Box>

      {/* Personel performansı */}
      <ChartCard
        title="Personel Performansı"
        subtitle="En çok talep tamamlayan 6 destekçi"
        delay={0.6}
      >
        {supporterPerf.length === 0 ? (
          <EmptyChart />
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={supporterPerf}
              layout="vertical"
              margin={{ top: 10, right: 30, left: 20, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis
                type="number"
                stroke="#64748B"
                fontSize={12}
                allowDecimals={false}
              />
              <YAxis
                type="category"
                dataKey="personel"
                stroke="#64748B"
                fontSize={12}
                width={110}
              />
              <Tooltip
                contentStyle={{
                  borderRadius: 8,
                  border: "1px solid #E2E8F0",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                }}
              />
              <Bar
                dataKey="tamamlanan"
                name="Tamamlanan"
                fill={successColor}
                radius={[0, 6, 6, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartCard>
    </Box>
  );
}

/* ─────────────── KPI Kartı ─────────────── */
function KpiCard({ icon: Icon, label, value, color, delay = 0 }) {
  return (
    <Paper
      elevation={0}
      sx={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        p: 2,
        borderRadius: 3,
        backgroundColor: "#fff",
        boxShadow: "0 8px 22px rgba(0,0,0,0.20)",
        border: "1px solid rgba(255,255,255,0.30)",
        animation: `${fadeInUp} 0.5s cubic-bezier(0.22, 1, 0.36, 1) ${delay}s backwards`,
        transition: "transform 0.25s ease, box-shadow 0.25s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 12px 28px rgba(0,0,0,0.25)",
        },
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: `${color}1A`,
          color,
          flexShrink: 0,
        }}
      >
        <Icon sx={{ fontSize: 22 }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: 20,
            lineHeight: 1.1,
            color: "#1E293B",
          }}
        >
          {value}
        </Typography>
        <Typography
          sx={{ fontSize: 11.5, fontWeight: 600, color: "#64748B" }}
          noWrap
        >
          {label}
        </Typography>
      </Box>
    </Paper>
  );
}

/* ─────────────── Chart Kartı ─────────────── */
function ChartCard({ title, subtitle, delay = 0, children }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 3,
        backgroundColor: "#fff",
        boxShadow: "0 8px 22px rgba(0,0,0,0.20)",
        border: "1px solid rgba(255,255,255,0.30)",
        animation: `${fadeInUp} 0.5s cubic-bezier(0.22, 1, 0.36, 1) ${delay}s backwards`,
      }}
    >
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontWeight: 800, fontSize: 15, color: "#1E293B" }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography
            sx={{
              fontSize: 11.5,
              color: "#64748B",
              fontWeight: 600,
              mt: 0.25,
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
      {children}
    </Paper>
  );
}

/* ─────────────── Boş grafik durumu ─────────────── */
function EmptyChart() {
  return (
    <Box
      sx={{
        height: 280,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#94A3B8",
        fontSize: 13,
        fontWeight: 600,
      }}
    >
      Gösterilecek veri yok
    </Box>
  );
}
